import type { FC } from 'react'
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import { motion } from 'framer-motion'

interface SmartImportProcessingProps {
  fileName: string
  importType: string
  statusMessage?: string
  title?: string
}

const SmartImportProcessing: FC<SmartImportProcessingProps> = ({
  fileName,
  importType,
  statusMessage,
  title = 'Smart Import in progress',
}) => {
  const theme = useTheme()
  const statusLabel = statusMessage || 'Preparing import'

  return (
    <Stack spacing={3} alignItems="center" textAlign="center" sx={{ width: '100%', maxWidth: 960 }}>
      {/* Text content */}
      <Stack spacing={1} alignItems="center" textAlign="center">
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        {/* Current processing state with animation */}
        <Box sx={{ height: 24, overflow: 'hidden' }}>
          <motion.div
            key={statusMessage || statusLabel}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <motion.span
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                }}
              />
              <motion.div
                key={statusLabel}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {statusLabel}
                </Typography>
              </motion.div>
            </Stack>
          </motion.div>
        </Box>

        {/* File info */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {importType}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            •
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileName}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default SmartImportProcessing
